import { LitElement, html, css, unsafeHTML } from 'https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js'

/**
 * Skio Universal Plan Picker
 * @docs https://integrate.skio.com/
 *
 * Uses LitJS (<5kb) https://lit.dev/ for reactive web components
 */

export class SkioPlanPicker extends LitElement {
  static properties = {
    key: { type: String },

    product: { type: Object },
    productHandle: { type: String },
    selectedVariant: { type: Object },

    options: { type: Object },

    moneyFormat: { type: String },

    quantity: { type: Number },
    rules: { type: Array },
    rule: { type: Object },
    cart: { type: Object },

    availableSellingPlanGroups: { state: true },
    selectedSellingPlanGroup: { type: Object },
    selectedSellingPlan: { type: Object },

    disableUrl: { type: Boolean },

    debug: { type: Boolean },
  }

  static styles = [
    css`
      :host {
        width: 100%;
        max-width: 440px;
      }

      .skio-plan-picker {
        display: flex;
        gap: 1rem;
        padding: 0;
        border: 0;
        font-size: 15px;
        color: black;
        width: 100%;
        margin: 0;
        font-family: inherit;
      }

      :host([layout='hidden']) .skio-plan-picker {
        display: none;
      }

      :host([layout='vertical']) .skio-plan-picker {
        flex-direction: column;
      }

      :host([layout='horizontal']) .skio-plan-picker {
        flex-direction: row;
        flex-wrap: wrap;
      }

      .sr-only {
        position: absolute;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        border: 0;
      }

      .group-container {
        display: block;
        position: relative;
        transition: border-color 0.2s ease;
        border: var(--skio-group-border-width, 1px) solid var(--skio-group-border-color, #ccc);
        border-radius: var(--skio-group-border-radius, 0);
        background-color: var(--skio-group-background-color, transparent);
        color: var(--skio-group-text-color, #000);
      }

      .group-container--selected {
        border-color: var(--skio-group-border-selected-color, #333);
        background-color: var(--skio-group-background-selected-color, transparent);
        color: var(--skio-group-text-selected-color, #000);
      }

      .group-container--last {
        order: 1;
      }

      :host([layout='horizontal']) .group-container {
        width: calc(50% - 0.5rem - calc(2 * var(--skio-group-border-width, 1px)));
      }

      .group-input {
        position: absolute;
        opacity: 0;
        width: 0px;
        height: 0px;
      }

      .group-input:focus-visible ~ .group-label {
        outline: 2px #ccc solid;
        outline-offset: 4px;
      }

      .group-label {
        display: flex;
        flex-direction: column;
        padding: 10px 16px;
        cursor: pointer;
      }

      .group-topline {
        display: flex;
        align-items: center;
        width: 100%;

        gap: 8px;
      }

      .group-container--selected .group-topline {
        font-weight: 700;
      }

      :host([layout='horizontal']) .group-topline {
        flex-direction: column;
        text-align: center;
      }

      .skio-radio__container {
        display: flex;
        color: var(--skio-group-border-color, #333);
      }

      .group-container--selected .skio-radio__container {
        color: var(--skio-group-border-selected-color, #333);
      }

      .skio-radio {
        transform-origin: center;
        opacity: 0;
        transform: scale(0);
        transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .group-label:hover .skio-radio {
        opacity: 0.75;
        transform: scale(1);
      }

      .group-container--selected .group-label .skio-radio {
        opacity: 1;
        transform: scale(1);
      }

      .skio-price {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        text-align: right;
        gap: 4px;

        line-height: 1;

        margin-left: auto;
        vertical-align: middle;

        font-weight: 600;
      }

      .skio-price s {
        font-size: 14px;

        font-weight: 400;
        opacity: 0.75;
      }

      :host([layout='horizontal']) .skio-price {
        text-align: center;
        margin-right: auto;
      }

      .group-content {
        display: flex;
        flex-direction: column;
        opacity: 1;
        width: auto;
        max-height: 1000px;
        transition: max-height 0.15s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .group-content.margin-left {
        margin-left: 0;
      }

      .group-content p {
        margin: 0.5rem 0 0 0;
        font-size: 13px;
        color: #000;
      }

      :host([layout='horizontal']) .group-content {
        width: 100%;
      }

      /* Hide frequency if not selected */
      .group-container:not(.group-container--selected) .group-content {
        pointer-events: none;
        opacity: 0;
        max-height: 0;
        visibility: hidden;
      }

      :host([layout='horizontal']) .group-container:not(.group-container--selected) + .group-content {
        pointer-events: none;
        opacity: 0;
        max-height: 0;
        visibility: hidden;
      }

      .group-title {
        width: 100%;
        max-width: 70%;
        line-height: 1.5;
        font-family: 'Morion';
        font-size: 16px;
      }

      :host([layout='horizontal']) .group-title {
        max-width: 100%;
      }

      .savings {
        color: var(--skio-discount-text-color, #fff);
      }

      .savings.bubble {
        padding: 0px 8px;
        background-color: var(--skio-discount-color, #0fa573);
        border: 1px var(--skio-discount-color, #0fa573) solid;
        color: var(--skio-discount-text-color, #fff);

        white-space: nowrap;

        font-size: 10px;
        font-weight: 400;
        text-transform: uppercase;
        padding: 7px 11px;
        border-radius: 50px;
        line-height: 12px;
        margin-left: 10px;
        color: white;
        text-wrap: nowrap;
        font-family: Apercu;
      }

      .selling-plan-dropdown {
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        display: flex;
        align-items: center;
        gap: 5px;
        width: 100%;
        margin: 5px 0 12px;
        padding: 12px 30px 12px 16px;
        background-color: #f2ede1;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 10px top 50%;
        background-size: 16px;
        border: none;
        font-size: 14px;
        text-overflow: ellipsis;
        white-space: nowrap;

        font-family: "Morion";
      }

      .selling-plan-dropdown-label {
        color: #000;
        font-weight: 500;
        font-size: 12px;
        padding-top: 8px;
      }

      .selling-plan-dropdown--one {
        background-image: none;
        pointer-events: none;
        background: transparent;
      }

      .selling-plan-buttons {
        display: flex;
        justify-content: space-between;
        gap: 10px;
        padding: 0.75rem 0;
        border: 0;
        font-size: 13px;
      }

      .selling-plan-buttons input[type='radio'] {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
      }

      .selling-plan-buttons label {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 0.8rem;
        cursor: pointer;
        border: 1px solid #ccc;
        text-align: center;
        padding: 12px;
        background-color: #f2ede1;
      }

      .selling-plan-buttons input[type='radio']:checked + label {
        border-color: #000;
      }

      .selling-plan-buttons input:focus-visible + label {
        outline: 2px #ccc solid;
        outline-offset: 4px;
      }

      .group-content-additional {
        margin-top: 5px;
      }

      .group-content-additional ul {
        list-style: none;
        padding: 0;
        margin: 0;
        background-color: #F2EDE1;
		    padding: 12px;
      }
      
      .group-content-additional ul li {
        margin-bottom: 8px;
        padding-left: 28px;
        position: relative;
      }

      .group-content-additional ul li:before {
        content: '';
        position: absolute;
        top: 0.25em;
        left: 0;
        background-color: #000;
        width: 18px;
        height: 18px;
        margin-top: -3px;
        mask-size: 100%;
        mask-position: center center;
        mask-repeat: no-repeat;
        mask-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTQiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNCAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTAuNDEyMTA5IDUuMjM2NzlIMTMuNTg4Nk0zLjcwNjIzIDguNTMwOTFINy4wMDAzNE0xMC4yOTQ1IDEuMTE5MTRWMi43NjYyTTMuNzA2MjMgMS4xMTkxNFYyLjc2NjJNMC40MTIxMDkgMS45NDI2N0gxMy41ODg2VjEzLjQ3MjFDMTMuNTg4NiAxMy45MDg5IDEzLjQxNTEgMTQuMzI3OCAxMy4xMDYyIDE0LjYzNjdDMTIuNzk3MyAxNC45NDU2IDEyLjM3ODMgMTUuMTE5MSAxMS45NDE1IDE1LjExOTFIMi4wNTkxN0MxLjYyMjM0IDE1LjExOTEgMS4yMDM0MSAxNC45NDU2IDAuODk0NTIyIDE0LjYzNjdDMC41ODU2MzggMTQuMzI3OCAwLjQxMjEwOSAxMy45MDg5IDAuNDEyMTA5IDEzLjQ3MjFWMS45NDI2N1oiIHN0cm9rZT0iIzBBMEEwQSIgc3Ryb2tlLXdpZHRoPSIwLjgyMzUyOSIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+Cjwvc3ZnPgo=);
      }

      .group-content-additional ul li:nth-of-type(2):before {
        mask-image: url(data:image/svg+xml;base64,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);
      }

      .group-content-additional ul li:nth-of-type(3):before {
        mask-image: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHBhdGggZD0iTTEwLjkyMTUgMTQuNzExNUwxNS42MDc0IDEwLjA0NjVMMTQuODMzNCA5LjI3MjQ2TDEwLjkyMTUgMTMuMTYzNUw5LjE1MDcxIDExLjQxMzVMOC4zNzY2OSAxMi4xODc1TDEwLjkyMTUgMTQuNzExNVpNMTIuMDAxNSAxOS41ODMzQzEwLjk2MjEgMTkuNTgzMyA5Ljk4MTQ4IDE5LjM4NiA5LjA1OTcxIDE4Ljk5MTJDOC4xMzc5MiAxOC41OTY1IDcuMzMxMSAxOC4wNTM0IDYuNjM5MjUgMTcuMzYxOUM1Ljk0NzM5IDE2LjY3MDMgNS40MDQwMSAxNS44NjM5IDUuMDA5MSAxNC45NDI1QzQuNjE0MiAxNC4wMjEyIDQuNDE2NzUgMTMuMDQwOCA0LjQxNjc1IDEyLjAwMTRDNC40MTY3NSAxMC45NDgxIDQuNjE0MTEgOS45NjQwNiA1LjAwODgzIDkuMDQ5MjNDNS40MDM1NSA4LjEzNDM4IDUuOTQ2NjggNy4zMzEwNCA2LjYzODIxIDYuNjM5MTlDNy4zMjk3NSA1Ljk0NzMzIDguMTM2MiA1LjQwMzk0IDkuMDU3NTYgNS4wMDkwNEM5Ljk3ODkxIDQuNjE0MTQgMTAuOTU5MyA0LjQxNjY5IDExLjk5ODcgNC40MTY2OUMxMy4wNTE5IDQuNDE2NjkgMTQuMDM2IDQuNjE0MDUgMTQuOTUwOCA1LjAwODc3QzE1Ljg2NTcgNS40MDM0OSAxNi42NjkgNS45NDY2MiAxNy4zNjA5IDYuNjM4MTVDMTguMDUyNyA3LjMyOTY5IDE4LjU5NjEgOC4xMzI2NyAxOC45OTEgOS4wNDcwOEMxOS4zODU5IDkuOTYxNDkgMTkuNTgzNCAxMC45NDUzIDE5LjU4MzQgMTEuOTk4NkMxOS41ODM0IDEzLjAzOCAxOS4zODYgMTQuMDE4NiAxOC45OTEzIDE0Ljk0MDRDMTguNTk2NiAxNS44NjIxIDE4LjA1MzQgMTYuNjY5IDE3LjM2MTkgMTcuMzYwOEMxNi42NzA0IDE4LjA1MjcgMTUuODY3NCAxOC41OTYxIDE0Ljk1MyAxOC45OTFDMTQuMDM4NiAxOS4zODU5IDEzLjA1NDcgMTkuNTgzMyAxMi4wMDE1IDE5LjU4MzNaTTEyLjAwMDEgMTguNUMxMy44MDU2IDE4LjUgMTUuMzQwMyAxNy44NjgxIDE2LjYwNDIgMTYuNjA0MkMxNy44NjgxIDE1LjM0MDMgMTguNTAwMSAxMy44MDU2IDE4LjUwMDEgMTJDMTguNTAwMSAxMC4xOTQ0IDE3Ljg2ODEgOC42NTk3MiAxNi42MDQyIDcuMzk1ODNDMTUuMzQwMyA2LjEzMTk0IDEzLjgwNTYgNS41IDEyLjAwMDEgNS41QzEwLjE5NDUgNS41IDguNjU5NzggNi4xMzE5NCA3LjM5NTg5IDcuMzk1ODNDNi4xMzIgOC42NTk3MiA1LjUwMDA2IDEwLjE5NDQgNS41MDAwNiAxMkM1LjUwMDA2IDEzLjgwNTYgNi4xMzIgMTUuMzQwMyA3LjM5NTg5IDE2LjYwNDJDOC42NTk3OCAxNy44NjgxIDEwLjE5NDUgMTguNSAxMi4wMDAxIDE4LjVaIiBmaWxsPSJjdXJyZW50Q29sb3IiPjwvcGF0aD48L3N2Zz4K);
      }

      .group-content-additional ul li:nth-of-type(4):before {
        mask-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjIiIGhlaWdodD0iMjIiIHZpZXdCb3g9IjAgLTIgMjIgMjIiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik04LjUxNzMzIDE0Ljk3MDFDOC41MTczMyAxNi4yMTkgNy41Mjg4OSAxNy4yMzIzIDYuMzEwMjIgMTcuMjMyM0M1LjA5MTU2IDE3LjIzMjMgNC4xMDMxMSAxNi4yMTkgNC4xMDMxMSAxNC45NjkyTTguNTE3MzMgMTQuOTcwMUM4LjUxNzMzIDEzLjcyMTIgNy41Mjg4OSAxMi43MDcgNi4zMTAyMiAxMi43MDdDNS4wOTE1NiAxMi43MDcgNC4xMDMxMSAxMy43MjAzIDQuMTAzMTEgMTQuOTY5Mk04LjUxNzMzIDE0Ljk3MDFMMTMuNDgyNyAxNC45NjkyTTQuMTAzMTEgMTQuOTY5MkgzVjUuNjc2NzZDMyA1LjQ0MTAxIDMuMDkzNjUgNS4yMTQ5MiAzLjI2MDM1IDUuMDQ4MjJDMy40MjcwNSA0Ljg4MTUyIDMuNjUzMTQgNC43ODc4NyAzLjg4ODg5IDQuNzg3ODdIMTIuNTkzOEMxMi44Mjk1IDQuNzg3ODcgMTMuMDU1NiA0Ljg4MTUyIDEzLjIyMjMgNS4wNDgyMkMxMy4zODkgNS4yMTQ5MiAxMy40ODI3IDUuNDQxMDEgMTMuNDgyNyA1LjY3Njc2VjcuNjE2MzJNMTMuNDgyNyAxNC45NjkySDE0LjAzNDdNMTMuNDgyNyAxNC45NjkyVjcuNjE2MzJNMTMuNDgyNyA3LjYxNjMySDE1LjQ5MjRDMTUuNzI5NSA3LjYxNjM0IDE1Ljk2NDEgNy42NjM3OCAxNi4xODI2IDcuNzU1ODNDMTYuNDAxIDcuODQ3ODggMTYuNTk4OSA3Ljk4MjY4IDE2Ljc2NDQgOC4xNTIzMkwxOSAxMC40NDQ4VjE0Ljk2OTJIMTguNDQ4TTE4LjQ0OCAxNC45NjkyQzE4LjQ0OCAxNi4yMTkgMTcuNDYwNCAxNy4yMzIzIDE2LjI0MTggMTcuMjMyM0MxNS4wMjMxIDE3LjIzMjMgMTQuMDM0NyAxNi4yMTkgMTQuMDM0NyAxNC45NjkyTTE4LjQ0OCAxNC45NjkyQzE4LjQ0OCAxMy43MjAzIDE3LjQ2MDQgMTIuNzA3IDE2LjI0MTggMTIuNzA3QzE1LjAyMzEgMTIuNzA3IDE0LjAzNDcgMTMuNzIwMyAxNC4wMzQ3IDE0Ljk2OTIiIHN0cm9rZT0iIzFFMUUxRSIgc3Ryb2tlLXdpZHRoPSIxLjA1IiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+Cg==);
      }

      .skio-details {
        --text-color: #333;
        --text-color-secondary: #888;

        user-select: none;
        -webkit-user-select: none;
        margin-bottom: 20px;
        order: 3;
        max-width: max-content;
      }

      .skio-details summary::-webkit-details-marker,
      .skio-details summary::marker,
      .skio-details slot {
        color: rgba(0, 0, 0, 0) !important;
      }

      .skio-details summary {
        margin-top: 15px;
      }

      .skio-details summary span {
        font-size: 0.9em;
        display: flex;
        padding: 0.5em 0;
        cursor: pointer;
        align-items: center;
        gap: 10px;
        text-decoration: underline;

        margin-top: -40px;
        color: #000;
      }

      @keyframes fadeInDown {
        0% {
          opacity: 0;
          transform: translateY(-15px);
        }
        100% {
          opacity: 1;
          transform: translateY(0px);
        }
      }
      .skio-details[open] > .skio-details--content {
        animation-name: fadeInDown;
        animation-duration: 0.3s;
      }

      .skio-details--content {
        position: absolute;
        z-index: 1020;
        padding: 1em;
        width: fit-content;
        border-radius: 5px;
        background: white;
        box-shadow: 0 0 5px rgb(23 24 24 / 5%), 0 1px 2px rgb(0 0 0 / 7%);
      }

      .skio-details ul {
        margin: 0;
        padding: 0;
      }

      .skio-details ul li {
        display: flex;
        align-items: flex-start;
        gap: 0.75em;

        margin-bottom: 1em;
      }

      .skio-details .skio-content {
        display: flex;
        flex-direction: column;
      }

      .skio-details .skio-content p {
        font-size: 0.9em;

        margin-top: 0;
        margin-bottom: 0;

        letter-spacing: 0;
        line-height: 1.5;

        color: var(--text-color);
      }

      .skio-details ul li small {
        font-size: 0.7em;
        color: var(--text-color-secondary);
      }

      .skio-details .skio-icon {
        display: flex;

        width: 2.25em;
        height: 2.25em;

        color: var(--text-color);
        background: #f8f8f8;
        border-radius: 100%;

        flex-shrink: 0;
        align-items: center;
        justify-content: center;
      }

      .skio-details .skio-icon svg {
        width: 1.25em;
        height: 1.25em;

        color: inherit;
      }

      .skio-details--footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 0.9em;
      }

      .skio-details--footer a {
        color: var(--text-color);
      }

      .skio-manage-link {
        text-decoration: underline;
      }

      .powered-by-skio {
        font-size: 0.8em;

        display: flex;
        text-decoration: none;

        align-items: center;
        gap: 3px;
      }

      .setupMode {
        position: relative;
        color: black;
        padding: 1rem;
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 1rem;
        font-size: 14px;
        border-style: solid;
        border-width: 4px;
        border-image: repeating-linear-gradient(-55deg, #000, #000 20px, #ffb101 20px, #ffb101 40px) 10;
      }

      .setupMode:after {
        content: 'Setup Mode';
        position: absolute;
        top: 0;
        right: 0;
        background-color: black;
        color: white;
        padding: 0.25rem 1rem;
        font-size: 13px;
      }

      .setupMode .detection {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 10px;
      }

      .setupMode .detection small {
        grid-column: 1/-1;
      }



      .selling-plan-button-icon {
        width: 14px;
        height: 14px;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 50%;
        border: 2px solid #888;
        background-color: transparent;
        transition: all 0.3s;
      }

      .selling-plan-buttons input[type='radio']:checked + label .selling-plan-button-icon {
        background-color: black;
        border: none;
        height: 15px;
        width: 15px;
      }

      .selling-plan-buttons input[type='radio']:checked + label .selling-plan-button-icon:before {
        content: "";
        width: 12px;
        height: 12px;
        border: 2px solid black;
        border-radius: 50%;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M5 13l4 4L19 7'/%3E%3C/svg%3E");
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        display: block;
      }

      @media screen and (max-width: 767px) {
      .skio-desktop-selector { display: none; }
      .skio-mobile-selector { display: block; }
      }

      @media screen and (min-width: 768px) {
        .skio-desktop-selector { display: block; }
        .skio-mobile-selector { display: none; }
      }
      @media screen and (max-width: 380px) {
        .savings.bubble {
          font-size: 9px;
          padding: 6px 8px;
          margin-left: 2px;
        }
      }

        .group-content-additional {
    background-color: #F2EDE1 !important;
  }
.free-gift-banner,
.glow-free-gift-banner{
  background-color: #C5E8D480;
  padding: 7px 7px;
  border: 1px solid #A8D8BD;
}

.gift-header {
  display: flex;
  margin-bottom: 5px;
}

.gift-icon {
  margin-right: 8px;
  color: #000;
}

.gift-title {
  font-weight: 700;
  font-size: 14px;
}

.gift-content {
  display: flex;
  align-items: center;
  gap: 5px;
}

.gift-image{
  height: 52px;
}

.gift-image img {
  max-width: 71px;
  height: 52px;
}

.gift-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.gift-product-title {
  font-weight: 700;
  font-size: 17px;
  letter-spacing: 0px;
  text-wrap: nowrap;
}

.gift-price {
  display: flex;
  align-items: center;
  gap: 8px;
}

.price-old {
  text-decoration: line-through;
  color: #585858;
  font-weight: 500;
  font-size: 14px;
}

.price-new {
  background-color: #708266;
  color: white;
  font-weight: 700;
  padding: 2px 4px;
  border-radius: 50px;
  font-size: 14px;
  line-height: normal;
}

@media screen and (max-width: 767px) {
  .gift-product-title {
    font-size: 12px;
    margin-bottom: 5px;
  }
}
  .prepaid-option .selling-plan-buttons,
  .prepaid-option .selling-plan-dropdown-label{
    display: none;
  }

  @media screen and (max-width: 767px) {
  .prepaid-option .savings.bubble {
    margin-left: 0;
  }
  .prepaid-option .selling-plan-dropdown{
    display: none;
   }
  .prepaid-option .savings.bubble {
    margin-left: 0;
  }
  .prepaid-option .group-title{
    margin-bottom: 10px;
  }
  }
    `,
  ]

  constructor() {
    super()

    // Start - Debug Variables
    this.debug = this.debug || window?.Shopify?.designMode
    this.variantChanged = false
    // End - Debug Variables

    this.rules = []
    this.rule = null
    this.quantity = 1
    this.showDetailsHover = false
    this.hasForcedDefaultGroup = false
    this.userSelectedPrepaid = false
  }

  async connectedCallback() {
    super.connectedCallback()

    this.log('Mounted')

    if (this.productHandle) {
      this.loading = true
      this.fetchProduct(this.productHandle)
    }

    this.bindFormEvents()
    this.bindCartEvents()

    this.rules = await this.fetchRules()
    await this.getRule()
  }

  // SECTION: Element Templates
  radioTemplate() {
    return html`
      <div class="skio-radio__container">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1"></circle>
          <circle class="skio-radio" cx="12" cy="12" r="7" fill="currentColor"></circle>
        </svg>
      </div>
    `
  }

  sellingPlanDropdown(group) {
    return html`
      <select
        class="selling-plan-dropdown ${this.getAvailableSellingPlans(group).length == 1 ? 'selling-plan-dropdown--one' : ''}"
        @change=${e => {
          this.selectSellingPlan(e.target.value)
        }}
      >
        ${group
          ? this.getAvailableSellingPlans(group).map(
              selling_plan => html` <option ?selected=${group.selected_selling_plan.id === selling_plan.id} value=${selling_plan.id}>${selling_plan.name}</option> `
            )
          : ''}
      </select>
    `
  }

  sellingPlanButtons(group) {
    return html`
      <fieldset class="selling-plan-buttons">
        <legend class="sr-only">Select subscription interval</legend>
        ${group
          ? this.getAvailableSellingPlans(group).map(
              selling_plan => html`
                <input
                  type="radio"
                  name="selling_plan_button"
                  value="${selling_plan}"
                  id="selling_plan_button-${selling_plan.id}"
                  @change="${e => this.selectSellingPlan(selling_plan.id)}"
                  ?checked=${group.selected_selling_plan === selling_plan}
                />

                <label for="selling_plan_button-${selling_plan.id}">
                  <span class="selling-plan-button-icon"></span>
                  <span>${selling_plan.name}</span>
                </label>
              `
            )
          : ''}
      </fieldset>
    `
  }

  invalidIcon() {
    return html`
      <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#D22B2B" class="w-6 h-6">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    `
  }

  validIcon() {
    return html`
      <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#0BDA51" class="w-6 h-6">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    `
  }

  setupMode() {
    return html`
      <div class="setupMode">
        Skio Plan Picker
        <small>This will not appear on the live site.</small>

        <small>Key: ${this.key}</small>

        <div class="detection">${this.product ? this.validIcon() : this.invalidIcon()} Product</div>

        <div class="detection">${this.selectedVariant ? this.validIcon() : this.invalidIcon()} Selected Variant</div>

        <div class="detection">${this.form ? this.validIcon() : this.invalidIcon()} Form</div>

        <div class="detection">${this.variantInput ? this.validIcon() : this.invalidIcon()} Variant Input</div>

        <div class="detection">
          ${this.variantChanged ? this.validIcon() : this.invalidIcon()} Variant Change Detection
          <small>Please attempt to change the selected variant.</small>
        </div>

        <small>If any of these don't pass please troubleshoot using our <a target="_blank" rel="noreferrer" href="https://integrate.skio.com/troubleshooting">troubleshooting guide</a>.</small>
      </div>
    `
  }

  groupContent(group) {
    return html`
      <div class="group-content ${this.options?.layout == 'horizontal' ? '' : this.options?.show_radio_selector && this.options?.dropdownPosition == 'inside' ? 'margin-left' : ''}">
        <span class="selling-plan-dropdown-label">${this.additionalFrequencyLabel()}</span>

        <div class="skio-mobile-selector">
          ${this.sellingPlanDropdown(group)} ${this.additionalContentText()}
        </div>

        <div class="skio-desktop-selector">
          ${this.sellingPlanButtons(group)} ${this.additionalContentText()}
        </div>
      </div>
    `
  }

  showDetails() {
    return html`
      <details class="skio-details" @mouseover=${e => this.detailsMouseover()} @mouseleave=${e => this.detailsMouseleave()}>
        <summary>
          <span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="ai ai-ArrowRepeat"
            >
              <path d="M18 2l3 3-3 3" />
              <path d="M6 22l-3-3 3-3" />
              <path d="M21 5H10a7 7 0 0 0-7 7" />
              <path d="M3 19h11a7 7 0 0 0 7-7" />
            </svg>

            How do subscriptions work?
          </span>
        </summary>
        <div class="skio-details--content">
          <ul>
            <li>
              <div class="skio-icon">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <div class="skio-content">
                <p>Get exclusive deals</p>
                <small>Subscribe for unique discounts</small>
              </div>
            </li>
            <li>
              <div class="skio-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </div>
              <div class="skio-content">
                <p>Edit your subscription anytime</p>
                <small>Edit products, delivery schedule and more</small>
              </div>
            </li>
            <li>
              <div class="skio-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>

              <div class="skio-content">
                <p>No commitment</p>
                <small>Easy to cancel if it’s not for you</small>
              </div>
            </li>
          </ul>
          <div class="skio-details--footer">
            <a class="skio-manage-link" href="/a/account/login">Manage subscriptions</a>

            <a style="letter-spacing: 0" class="powered-by-skio" href="https://skio.com/?utm_source=eonsincshop.myshopify.com&utm_medium=details_popover" target="_blank" rel="noopener">
              Powered by
              <svg width="24" height="11" viewBox="0 0 24 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4.28399 5.78801C4.12399 5.63601 3.93599 5.50801 3.71999 5.40401C3.50399 5.30001 3.27599 5.24801 3.03599 5.24801C2.85199 5.24801 2.67999 5.28401 2.51999 5.35601C2.36799 5.42801 2.29199 5.55201 2.29199 5.72801C2.29199 5.89601 2.37599 6.01601 2.54399 6.08801C2.71999 6.16001 2.99999 6.24001 3.38399 6.32801C3.60799 6.37601 3.83199 6.44401 4.05599 6.53201C4.28799 6.62001 4.49599 6.73601 4.67999 6.88001C4.86399 7.02401 5.01199 7.20001 5.12399 7.40801C5.23599 7.61601 5.29199 7.86401 5.29199 8.15201C5.29199 8.52801 5.21599 8.84801 5.06399 9.11201C4.91199 9.36801 4.71199 9.57601 4.46399 9.73601C4.22399 9.89601 3.95199 10.012 3.64799 10.084C3.34399 10.156 3.03999 10.192 2.73599 10.192C2.24799 10.192 1.76799 10.116 1.29599 9.96401C0.831989 9.80401 0.443989 9.57201 0.131989 9.26801L1.23599 8.10401C1.41199 8.29601 1.62799 8.45601 1.88399 8.58401C2.13999 8.71201 2.41199 8.77601 2.69999 8.77601C2.85999 8.77601 3.01599 8.74001 3.16799 8.66801C3.32799 8.58801 3.40799 8.45201 3.40799 8.26001C3.40799 8.07601 3.31199 7.94001 3.11999 7.85201C2.92799 7.76401 2.62799 7.67201 2.21999 7.57601C2.01199 7.52801 1.80399 7.46401 1.59599 7.38401C1.38799 7.30401 1.19999 7.19601 1.03199 7.06001C0.871989 6.92401 0.739989 6.75601 0.635989 6.55601C0.531989 6.35601 0.479989 6.11601 0.479989 5.83601C0.479989 5.47601 0.555989 5.17201 0.707989 4.92401C0.859989 4.66801 1.05599 4.46001 1.29599 4.30001C1.53599 4.14001 1.79999 4.02401 2.08799 3.95201C2.38399 3.87201 2.67599 3.83201 2.96399 3.83201C3.41199 3.83201 3.84799 3.90401 4.27199 4.04801C4.70399 4.18401 5.06799 4.39201 5.36399 4.67201L4.28399 5.78801Z"
                  fill="black"
                />
                <path d="M12.8481 10H10.4121L8.45615 7.13201H8.42015V10H6.44015V0.928009H8.42015V6.44801H8.45615L10.3641 4.02401H12.7521L10.4481 6.72401L12.8481 10Z" fill="black" />
                <path
                  d="M15.7009 2.11601C15.7009 2.26801 15.6689 2.41201 15.6049 2.54801C15.5489 2.67601 15.4689 2.78801 15.3649 2.88401C15.2689 2.98001 15.1489 3.05601 15.0049 3.11201C14.8689 3.16801 14.7249 3.19601 14.5729 3.19601C14.2529 3.19601 13.9849 3.09201 13.7689 2.88401C13.5529 2.66801 13.4449 2.41201 13.4449 2.11601C13.4449 1.97201 13.4729 1.83601 13.5289 1.70801C13.5849 1.57201 13.6649 1.45601 13.7689 1.36001C13.8729 1.26401 13.9929 1.18801 14.1289 1.13201C14.2649 1.06801 14.4129 1.03601 14.5729 1.03601C14.7249 1.03601 14.8689 1.06401 15.0049 1.12001C15.1489 1.17601 15.2689 1.25201 15.3649 1.34801C15.4689 1.44401 15.5489 1.56001 15.6049 1.69601C15.6689 1.82401 15.7009 1.96401 15.7009 2.11601ZM13.5889 10V4.02401H15.5569V10H13.5889Z"
                  fill="black"
                />
                <path
                  d="M23.4516 6.98801C23.4516 7.47601 23.3636 7.92001 23.1876 8.32001C23.0116 8.71201 22.7716 9.04801 22.4676 9.32801C22.1636 9.60001 21.8116 9.81201 21.4116 9.96401C21.0116 10.116 20.5836 10.192 20.1276 10.192C19.6796 10.192 19.2516 10.116 18.8436 9.96401C18.4436 9.81201 18.0916 9.60001 17.7876 9.32801C17.4916 9.04801 17.2556 8.71201 17.0796 8.32001C16.9036 7.92001 16.8156 7.47601 16.8156 6.98801C16.8156 6.50001 16.9036 6.06001 17.0796 5.66801C17.2556 5.27601 17.4916 4.94401 17.7876 4.67201C18.0916 4.40001 18.4436 4.19201 18.8436 4.04801C19.2516 3.90401 19.6796 3.83201 20.1276 3.83201C20.5836 3.83201 21.0116 3.90401 21.4116 4.04801C21.8116 4.19201 22.1636 4.40001 22.4676 4.67201C22.7716 4.94401 23.0116 5.27601 23.1876 5.66801C23.3636 6.06001 23.4516 6.50001 23.4516 6.98801ZM21.5556 6.98801C21.5556 6.79601 21.5236 6.60801 21.4596 6.42401C21.3956 6.24001 21.3036 6.08001 21.1836 5.94401C21.0636 5.80001 20.9156 5.68401 20.7396 5.59601C20.5636 5.50801 20.3596 5.46401 20.1276 5.46401C19.8956 5.46401 19.6916 5.50801 19.5156 5.59601C19.3396 5.68401 19.1916 5.80001 19.0716 5.94401C18.9596 6.08001 18.8716 6.24001 18.8076 6.42401C18.7516 6.60801 18.7236 6.79601 18.7236 6.98801C18.7236 7.18001 18.7516 7.36801 18.8076 7.55201C18.8716 7.73601 18.9636 7.90401 19.0836 8.05601C19.2036 8.20001 19.3516 8.31601 19.5276 8.40401C19.7036 8.49201 19.9076 8.53601 20.1396 8.53601C20.3716 8.53601 20.5756 8.49201 20.7516 8.40401C20.9276 8.31601 21.0756 8.20001 21.1956 8.05601C21.3156 7.90401 21.4036 7.73601 21.4596 7.55201C21.5236 7.36801 21.5556 7.18001 21.5556 6.98801Z"
                  fill="black"
                />
              </svg>
            </a>
          </div>
        </div>
      </details>
    `
  }
  isUKMarket() {
    const currency = window?.Shopify?.currency?.active || window?.Shopify?.currency?.currency
    if (currency) return String(currency).toUpperCase() === 'GBP'
    return !!(this.moneyFormat && this.moneyFormat.includes('£'))
  }

  isDeepWinterVariant() {
    const title = (this.selectedVariant?.title || '').toLowerCase()
    return title.includes('deep winter')
  }

  shouldUseDeepWinterFakeDiscount() {
    return this.options?.show_compare_price && this.isUKMarket() && this.isDeepWinterVariant()
  }

  deepWinterDiscountedPriceCents() {
    const price = Number(this.selectedVariant?.price || 0)
    // 20% off
    return Math.round(price * 0.8)
  }

  oneTimeCrossedPriceCents() {
    // If Deep Winter fake discount: cross the current price
    if (this.shouldUseDeepWinterFakeDiscount()) return Number(this.selectedVariant?.price || 0)

    // Otherwise keep existing compare-at behavior
    const price = Number(this.selectedVariant?.price || 0)
    const compareAt = Number(this.selectedVariant?.compare_at_price || 0)
    return this.options?.show_compare_price && compareAt > price ? compareAt : 0
  }

  oneTimeDisplayedPriceCents() {
    // If Deep Winter fake discount: show the 20% off price
    if (this.shouldUseDeepWinterFakeDiscount()) return this.deepWinterDiscountedPriceCents()

    // Otherwise show normal price
    return Number(this.selectedVariant?.price || 0)
  }

  // !SECTION: Element Templates

  render() {
    if (!this.viable() || this.options?.layout === 'hidden') {
      return this.debug ? this.setupMode() : ''
    }

    if (this.selectedVariant.selling_plan_allocations.length === 0 && !this.options?.show_without_subscription) {
      return this.debug ? this.setupMode() : ''
    }

const allGroups = this.availableSellingPlanGroups || [];
const regularGroups = allGroups.filter(g => g.name !== "Prepaid" && g.name !== "One-time");
const prepaidGroups = allGroups.filter(g => g.name === "Prepaid");

return html`
  ${this.debug ? this.setupMode() : null}

  <fieldset class="skio-plan-picker" role="radiogroup" aria-labelledby="skio-plan-picker-legend">
    <legend id="skio-plan-picker-legend" class="${!this.options?.show_legend ? 'sr-only' : ''}">
      ${unsafeHTML(this.options?.legend_content)}
    </legend>

    ${regularGroups.length
      ? regularGroups.map(
          (group, index) => html`
            <div class="group-container ${this.selectedSellingPlanGroup == group ? 'group-container--selected' : ''}">
              <input
                id="group-${index}-${this.key}"
                class="group-input"
                name="skio-group-${this.key}"
                type="radio"
                value="${group.id}"
                @change=${() => {
                  this.userSelectedPrepaid = false; // user chose normal subscribe
                  this.selectSellingPlanGroup(group);
                }}
                ?checked=${this.selectedSellingPlanGroup == group}
              />

              <label class="group-label" for="group-${index}-${this.key}">
                <div class="group-topline">
                  ${this.options?.layout == 'horizontal'
                    ? ''
                    : this.options?.show_radio_selector
                    ? this.radioTemplate()
                    : ''}

                  <div class="group-title">
                    ${group.name !== 'Prepaid' && this.options?.subscription_title
                      ? this.options?.subscription_title
                      : group.name === 'Prepaid' && this.options?.prepaid_title
                      ? this.options?.prepaid_title
                      : group.name}

                    ${group.name === 'Prepaid'
                      ? html`<span class="savings ${this.options?.discount_style}">Save 30%</span>`
                      : this.discountText(group.selected_selling_plan)
                      ? html`<span class="savings ${this.options?.discount_style}">
                          ${this.discountText(group.selected_selling_plan)}
                        </span>`
                      : html``}
                  </div>

                  <div class="skio-price" aria-live="polite">
                    ${this.options?.show_compare_price &&
                    (this.selectedVariant?.compare_at_price > this.selectedVariant.price ||
                      this.selectedVariant.price > this.price(group.selected_selling_plan, false))
                      ? html`<s aria-hidden="true">
                          ${this.money(
                            this.selectedVariant.compare_at_price
                              ? this.selectedVariant.compare_at_price
                              : this.selectedVariant.price
                          )}
                        </s>`
                      : ''}
                    ${this.price(group.selected_selling_plan)}
                  </div>
                </div>

                ${this.options?.layout == 'vertical' && this.options?.dropdownPosition == 'inside'
                  ? this.groupContent(group)
                  : ''}
              </label>
            </div>

            ${this.selectedSellingPlanGroup == group
              ? this.options?.layout == 'horizontal' || this.options?.dropdownPosition == 'underneath'
                ? this.groupContent(group)
                : ''
              : ''}
          `
        )
      : ''}

    ${!this.product.requires_selling_plan
      ? html`
          <div
            class="group-container ${this.selectedSellingPlanGroup == null ? 'group-container--selected' : ''} ${!this.options
              ?.onetime_first && !prepaidGroups.length
              ? 'group-container--last'
              : ''}"
            @click=${() => {
              this.userSelectedPrepaid = false; // one-time, not prepaid
              this.selectSellingPlanGroup(null);
            }}
          >
            <input
              id="one-time-${this.key}"
              class="group-input"
              name="skio-group-${this.key}"
              type="radio"
              value="One time purchase"
              ?checked=${!this.selectedSellingPlanGroup}
              @change=${() => {
                this.userSelectedPrepaid = false;
                this.selectSellingPlanGroup(null);
              }}
            />

            <label class="group-label one-time-group-label" for="one-time-${this.key}">
              <div class="group-topline">
                ${this.options?.layout == 'horizontal'
                  ? ''
                  : this.options?.show_radio_selector
                  ? this.radioTemplate()
                  : ''}

                <div class="group-title">
                  ${this.options?.onetime_title ? this.options?.onetime_title : 'One time purchase'}
                </div>

<div class="skio-price" aria-live="polite">
  ${this.oneTimeCrossedPriceCents()
    ? html`<s aria-hidden="true">${this.money(this.oneTimeCrossedPriceCents())}</s>`
    : ''}
  ${this.money(this.oneTimeDisplayedPriceCents())}
</div>
              </div>
            </label>
          </div>
        `
      : ''}

    ${this.options?.show_details == true ? this.showDetails() : ''}

    ${prepaidGroups.length
      ? prepaidGroups.map(
          (group, index) => {
            const globalIndex = regularGroups.length + index;

            return html`
              <div
                class="group-container prepaid-option
                  ${this.selectedSellingPlanGroup == group ? 'group-container--selected' : ''}
                  ${!this.options?.onetime_first && index === prepaidGroups.length - 1 ? 'group-container--last' : ''}"
              >
                <input
                  id="group-${globalIndex}-${this.key}"
                  class="group-input"
                  name="skio-group-${this.key}"
                  type="radio"
                  value="${group.id}"
                  @change=${() => {
                    this.userSelectedPrepaid = true; // ✅ user explicitly picked Prepaid
                    this.selectSellingPlanGroup(group);
                  }}
                  ?checked=${this.selectedSellingPlanGroup == group}
                />

                <label class="group-label" for="group-${globalIndex}-${this.key}">
                  <div class="group-topline">
                    ${this.options?.layout == 'horizontal'
                      ? ''
                      : this.options?.show_radio_selector
                      ? this.radioTemplate()
                      : ''}

                    <div class="group-title">
                      ${group.name !== 'Prepaid' && this.options?.subscription_title
                        ? this.options?.subscription_title
                        : group.name === 'Prepaid' && this.options?.prepaid_title
                        ? this.options?.prepaid_title
                        : group.name}

                      ${group.name === 'Prepaid'
                        ? html`<span class="savings ${this.options?.discount_style}">Save 30%</span>`
                        : this.discountText(group.selected_selling_plan)
                        ? html`<span class="savings ${this.options?.discount_style}">
                            ${this.discountText(group.selected_selling_plan)}
                          </span>`
                        : html``}
                    </div>

                    <div class="skio-price" aria-live="polite">
                      ${this.options?.show_compare_price
                        ? html`<s aria-hidden="true">
                            ${this.money(
                              Math.round(
                                this.price(group.selected_selling_plan, false) / 0.7
                              )
                            )}
                          </s>`
                        : ''}

                      ${this.price(group.selected_selling_plan)}
                    </div>
                  </div>

                  ${this.options?.layout == 'vertical' && this.options?.dropdownPosition == 'inside'
                    ? this.groupContent(group)
                    : ''}
                </label>
              </div>

              ${this.selectedSellingPlanGroup == group
                ? this.options?.layout == 'horizontal' || this.options?.dropdownPosition == 'underneath'
                  ? this.groupContent(group)
                  : ''
                : ''}
            `;
          }
        )
      : ''}
  </fieldset>
`;

  }

  async fetchRules() {
    if (sessionStorage.getItem('skio-discount-rules')) {
      return JSON.parse(sessionStorage.getItem('skio-discount-rules'))
    } else {
      const rules = await fetch(`https://api.skio.com/storefront-http/get-rules-by-domain-or-hostname?domain=${window?.Shopify?.shop}`)
        .then(response => response.json())
        .then(response => {
          return response.rules.filter(rule => rule.type !== 'surpriseDelight').sort((a, b) => a.minQuantityToDiscount - b.minQuantityToDiscount)
        })

      sessionStorage.setItem('skio-discount-rules', JSON.stringify(rules))
      return rules
    }
  }

  eligibleItemCount(rule) {
    const eligibleQuantity = rule?.productVariantIds.some(gid => gid.includes(this.selectedVariant.id)) ? this.quantity : 0

    if (!this.cart?.items) return eligibleQuantity

    const cartItems = Number(
      this.cart?.items.reduce(
        (aggregate, item) =>
          rule?.productVariantIds.some(gid => gid.includes(item.id) && item.selling_plan_allocation && gid.includes(this.selectedVariant.id)) ? aggregate + item.quantity : aggregate,
        0
      )
    )

    return cartItems + eligibleQuantity
  }

  getRule() {
    let largestMinQuantity = {
      value: -1,
      index: -1,
    }

    this.rules.forEach((rule, index) => {
      if (rule.code && largestMinQuantity.value < rule.minQuantityToDiscount) {
        if (rule.minQuantityToDiscount <= this.eligibleItemCount(rule)) {
          largestMinQuantity.value = rule.minQuantityToDiscount
          largestMinQuantity.index = index
        }
      }
    })

    this.rule = largestMinQuantity.index > -1 ? this.rules[largestMinQuantity.index] : null
  }

  bindCartEvents() {
    document.addEventListener('CartUpdated', event => {
      this.cart = event.detail
    })
  }

bindFormEvents() {
  // Find the form Skio is attached to
  this.form =
    document.querySelector(`#${this.options?.form_id}`) ||
    this.closest('form[action*="/cart/add"]')

  if (!this.form) return

  // Hidden Shopify variant id input
  this.variantInput = this.form.querySelector('[name="id"]')

  const updateVariantFromVariantId = () => {
    if (!this.product || !this.variantInput) return

    const newVariant = this.product.variants.find(
      v => v.id == this.variantInput.value
    )

    if (newVariant && (!this.selectedVariant || newVariant.id !== this.selectedVariant.id)) {
      this.selectedVariant = newVariant
      this.variantChanged = true
    }
  }

  const updateVariantFromOptions = () => {
    if (!this.product) return

    // Read currently selected Size + Fragrance radios (they use `form=""` attr)
    const sizeInput = document.querySelector('input[name="Size"]:checked')
    const fragranceInput = document.querySelector('input[name="Fragrance"]:checked')

    const sizeValue = sizeInput ? sizeInput.value : null
    const fragranceValue = fragranceInput ? fragranceInput.value : null

    if (!sizeValue) return

    // Match against product.variants option1 / option2
    const newVariant = this.product.variants.find(v => {
      const matchSize = v.option1 === sizeValue
      const matchFrag = fragranceValue ? v.option2 === fragranceValue : true
      return matchSize && matchFrag
    })

    if (newVariant && (!this.selectedVariant || newVariant.id !== this.selectedVariant.id)) {
      this.selectedVariant = newVariant
      this.variantChanged = true
    }
  }

  // Still listen to id changes (for theme’s own logic)
  if (this.variantInput) {
    this.variantInput.addEventListener('change', updateVariantFromVariantId)
  }

  // 🔗 Listen globally for Size / Fragrance radio changes that belong to this form
  document.addEventListener('change', e => {
    const target = e.target
    if (!(target instanceof HTMLInputElement)) return
    if (target.type !== 'radio') return
    if (target.name !== 'Size' && target.name !== 'Fragrance') return

    // Make sure this radio is for OUR form (it uses the `form=""` attr)
    const formAttr = target.getAttribute('form')
    if (this.options?.form_id && formAttr && formAttr !== this.options.form_id) return

    updateVariantFromOptions()
  })

  // Quantity (unchanged)
  const $quantityInput = this.form.querySelector('[name="quantity"]')

  if ($quantityInput) {
    this.quantity = Number($quantityInput.value)
    $quantityInput.addEventListener('input', () => {
      this.quantity = Number($quantityInput.value)
    })
  }
}

  detailsMouseover() {
    let details = this.renderRoot.querySelector('.skio-details')
    let summary = this.renderRoot.querySelector('.skio-details summary')
    if (!details.hasAttribute('open') && this.showDetailsHover == false) {
      summary.click()
      this.showDetailsHover = true
    }
  }

  detailsMouseleave() {
    let details = this.renderRoot.querySelector('.skio-details')
    let summary = this.renderRoot.querySelector('.skio-details summary')
    if (details.hasAttribute('open') && this.showDetailsHover == true) {
      summary.click()
      this.showDetailsHover = false
    }
  }

  updated(changed) {
    if (!this.viable()) return

    if (changed.has('product') && this.product) {
      this.key = this.key ? this.key : this.product.id
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {
      const filteredSellingPlanGroups = this.product.selling_plan_groups.filter(
        selling_plan_group =>
          selling_plan_group.app_id === 'SKIO' && !selling_plan_group.name.toLowerCase().includes('hidden-') && !selling_plan_group.name.toLowerCase().includes('dynamic box subscription')
      )

      //update availableSellingPlanGroups based on skioSellingPlanGroups and selectedVariant.id
      this.availableSellingPlanGroups = filteredSellingPlanGroups.filter(selling_plan_group =>
        selling_plan_group.selling_plans.some(selling_plan =>
          this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
        )
      )

      if (this.options?.combine_groups) {
        this.availableSellingPlanGroups = [
          {
            name: this.options?.combined_group_name,
            selling_plans: this.availableSellingPlanGroups.flatMap(group => group.selling_plans),
            id: 'combined_group',
            app_id: 'SKIO',
          },
        ]
      }
if (this.availableSellingPlanGroups?.length) {
  const url = new URL(window.location.href);
  const urlSelectedSellingPlanId = url.searchParams.get('selling_plan');
  const hasUrlPlan = !!urlSelectedSellingPlanId;

  // First, let Skio figure out per-group default plan (URL / default_subscription)
  this.availableSellingPlanGroups.forEach(group => {
    const availableSellingPlans = this.getAvailableSellingPlans(group);

    const urlSelectedSellingPlan = hasUrlPlan
      ? availableSellingPlans.find(plan => plan.id == urlSelectedSellingPlanId)
      : null;

    const nameSelectedSellingPlan = availableSellingPlans.find(
      plan => plan.name === this.selectedSellingPlan?.name
    );

    const defaultSellingPlan =
      this.options?.default_subscription && this.options.default_subscription.trim() !== ''
        ? availableSellingPlans.find(plan =>
            plan.name.toLowerCase().includes(this.options.default_subscription.toLowerCase())
          )
        : null;

    group.selected_selling_plan =
      urlSelectedSellingPlan || nameSelectedSellingPlan || defaultSellingPlan || availableSellingPlans[0];

    const isAnyPlanSelected = urlSelectedSellingPlan || nameSelectedSellingPlan || defaultSellingPlan;
    group.selected = !!isAnyPlanSelected;
  });

  // 🥇 If URL explicitly chooses a plan, respect it entirely (even if it's Prepaid)
  if (hasUrlPlan) {
    const urlGroup = this.availableSellingPlanGroups.find(group =>
      group.selling_plans.some(plan => plan.id == urlSelectedSellingPlanId)
    );

    if (urlGroup) {
      this.selectSellingPlan(urlSelectedSellingPlanId);
      return;
    }
  }
  if (!hasUrlPlan && !this.product.requires_selling_plan) {
  this.selectSellingPlanGroup(null);
  return;
}

  // 🥈 Otherwise: pick a default group
  const regularGroups = this.availableSellingPlanGroups.filter(
    g => g.name !== 'Prepaid' && g.name !== 'One-time'
  );

  // – Try: a selected NON-Prepaid group
  let selectedSellingPlanGroup =
    this.availableSellingPlanGroups.find(g => g.selected && g.name !== 'Prepaid') ||
    // – Else: first regular (Subscribe) group
    regularGroups[0] ||
    // – Else: any selected group (fallback)
    this.availableSellingPlanGroups.find(g => g.selected) ||
    // – Else: first group
    this.availableSellingPlanGroups[0];

  const defaultPlan =
    selectedSellingPlanGroup.selected_selling_plan ||
    this.getAvailableSellingPlans(selectedSellingPlanGroup)[0];

  this.selectSellingPlan(defaultPlan.id);
} else {
  // No subscription groups at all → one-time only
  this.selectSellingPlanGroup(null);
}
    }

    if (changed.has('selectedSellingPlan')) {
      //dispatch CustomEvent to tell that this specific plan picker was updated, and pass the selectedSellingPlan
      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true,
        composed: true,
        detail: {
          variant: this.selectedVariant,
          sellingPlan: this.selectedSellingPlan,
          key: this.key,
        },
      })

      this.dispatchEvent(event)

      this.dispatchEvent(
        new CustomEvent('change', {
          detail: { value: this.selectedSellingPlan?.id },
        })
      )
    }

    this.updateForm()
    this.updateExternalPrice()
    this.updateExternalCompareAtPrice()

    if (!this.disableUrl) {
      this.updateURLParams()
    }

    if (changed.has('quantity')) {
      this.getRule()
    }

    if (changed.has('cart')) {
      this.getRule()
    }

    this.updateExternalElements()

    this.removeExternalSellingPlanInputs()
  }

  removeExternalSellingPlanInputs() {
    // Remove any non-Skio selling plan inputs
    const removeNonSkioInputs = (inputs) => {
      inputs.forEach(input => {
        if (!input.classList.contains('skio-selling-plan-input')) {
          input.remove()
        }
      })
    }

    // Handle initial inputs
    removeNonSkioInputs(document.querySelectorAll('input[name="selling_plan"]'))

    // Watch for new selling plan inputs being added
    new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.matches('input[name="selling_plan"]')) {
              removeNonSkioInputs([node])
            }
            removeNonSkioInputs(node.querySelectorAll('input[name="selling_plan"]'))
          }
        })
      })
    }).observe(document.body, {
      childList: true,
      subtree: true
    })
  }

  updateExternalElements() {
    document.querySelectorAll(`[skio-price][skio-key="${this.key}"]`).forEach(el => {
      el.innerHTML = this.price(this.selectedSellingPlan)
    })

    document.querySelectorAll(`[skio-subscription-content]`).forEach(el => {
      el.style.display = this.selectedSellingPlan ? 'block' : 'none'
    })
  }

  // SECTION: Utility Functions
  log(...args) {
    args.unshift(`%c[skio plan picker][${this.key}]`, 'color: #8770f2;')
    console.log.apply(console, args)
  }

  error(...args) {
    args.unshift(`%c [skio plan picker][${this.key}]`, 'color: #ff0000')
    console.error.apply(console, args)
  }

  appendOrdinalSuffix(num) {
    const j = num % 10,
      k = num % 100
    if (j == 1 && k != 11) {
      return num + 'st'
    }
    if (j == 2 && k != 12) {
      return num + 'nd'
    }
    if (j == 3 && k != 13) {
      return num + 'rd'
    }
    return num + 'th'
  }

  viable() {
    const errors = []

    const sellingPlanInput = this.querySelector('input[name=selling_plan]')
    if (!sellingPlanInput) {
      errors.push('Missing selling plan input')
    }

    if (!this.product) {
      errors.push('No product found. Please pass a product or productHandle to the skio-plan-picker component.')
    }
    if (!this.selectedVariant) {
      errors.push('No variant found. Please pass a product or productHandle to the skio-plan-picker component.')
    }
    if (!this.form) {
      errors.push('No form found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id".')
    }
    if (!this.variantInput) {
      errors.push('No variant input found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id"')
    }

    if (errors.length == 0) return true

    this.error('Errors', errors)
    return false
  }

  getAvailableSellingPlans(group) {
    let availableSellingPlans = group.selling_plans.filter(selling_plan =>
      this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
    )

    return availableSellingPlans
  }

  // !SECTION: Utility Functions

  // Update selected selling plan group; called on click of group-container element
  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group
    this.selectedSellingPlan = group?.selected_selling_plan
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(selling_plan_id) {
    const selectedGroup = this.availableSellingPlanGroups.find(group => group.selling_plans.some(plan => plan.id == selling_plan_id))
    const selectedSellingPlan = selectedGroup.selling_plans.find(plan => plan.id == selling_plan_id)

    selectedGroup.selected_selling_plan = selectedSellingPlan
    this.selectedSellingPlanGroup = selectedGroup
    this.selectedSellingPlan = selectedSellingPlan
  }

  updateExternalPrice() {
    document.querySelectorAll(this.options?.external_price_selector).forEach(el => {
      this.selectedSellingPlan ? (el.innerHTML = this.price(this.selectedSellingPlan)) : (el.innerHTML = this.money(this.selectedVariant.price))
    })
  }

  updateExternalCompareAtPrice() {
    const elements = document.querySelectorAll(
      this.options?.external_compare_at_price_price_selector
    );

    if (!elements.length) return;

    let compareCents = 0;

    if (this.selectedSellingPlan) {
      // Subscription selected
      const group = this.selectedSellingPlanGroup;

      if (group?.name === 'Prepaid') {
        // Mirror internal prepaid logic:
        // <s> money(Math.round(price(group.selected_selling_plan,false) / 0.7)) </s>
        const subscriptionPrice = this.price(group.selected_selling_plan, false);
        if (subscriptionPrice) {
          compareCents = Math.round(subscriptionPrice / 0.7);
        }
      } else if (this.options?.show_compare_price) {
        // Normal subscription group logic – mirror internal Skio check
        const subscriptionPrice = this.price(this.selectedSellingPlan, false);
        const variantPrice = this.selectedVariant?.price || 0;
        const variantCompare = this.selectedVariant?.compare_at_price || 0;

        if (variantCompare && variantCompare > variantPrice) {
          // Use Shopify compare_at_price if it's higher than price
          compareCents = variantCompare;
        } else if (variantPrice && variantPrice > subscriptionPrice) {
          // Or use Shopify price if it's higher than subscription price
          compareCents = variantPrice;
        }
      }
    } else {
      // One-time selected
      if (
        this.options?.show_compare_price &&
        this.selectedVariant?.compare_at_price > this.selectedVariant.price
      ) {
        compareCents = this.selectedVariant.compare_at_price;
      }
    }

    elements.forEach(el => {
      el.innerHTML = compareCents ? this.money(compareCents) : '';
    });
  }

  additionalFrequencyLabel() {
    if (!this.options?.additional_frequency_label || this.options?.additional_frequency_label == '') return

    return unsafeHTML(this.options?.additional_frequency_label)
  }

  additionalContentText() {
    if (!this.options?.additional_subscription_content || this.options?.additional_subscription_content == '') return

    return html`
      <div class="group-content-additional">
<div style="display: none;" class="free-gift-banner">
  <div class="gift-header">
    <div class="gift-icon">
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
      <g clip-path="url(#clip0_36_3401)">
      <path d="M16.418 4.21875H14.272C14.6038 3.77775 14.8008 3.22984 14.8008 2.63672C14.8008 1.18283 13.6179 0 12.1641 0C11.2883 0 10.6425 0.31377 10.1317 0.987434C9.70439 1.55106 9.39192 2.338 9 3.33229C8.60808 2.33796 8.29561 1.55106 7.86829 0.987434C7.3575 0.31377 6.71171 0 5.83594 0C4.38205 0 3.19922 1.18283 3.19922 2.63672C3.19922 3.22984 3.3962 3.77775 3.728 4.21875H1.58203C0.709699 4.21875 0 4.92845 0 5.80078V6.85547C0 7.54295 0.440895 8.12911 1.05469 8.3468V16.418C1.05469 17.2903 1.76439 18 2.63672 18H15.3633C16.2356 18 16.9453 17.2903 16.9453 16.418V8.3468C17.5591 8.12911 18 7.54295 18 6.85547V5.80078C18 4.92845 17.2903 4.21875 16.418 4.21875ZM9.96799 3.75258C10.7758 1.70304 11.083 1.05469 12.1641 1.05469C13.0364 1.05469 13.7461 1.76439 13.7461 2.63672C13.7461 3.50905 13.0364 4.21875 12.1641 4.21875H9.78353C9.84814 4.0565 9.90977 3.9003 9.96799 3.75258ZM5.83594 1.05469C6.91703 1.05469 7.22415 1.70304 8.03201 3.75258C8.09023 3.9003 8.15186 4.0565 8.21647 4.21875H5.83594C4.96361 4.21875 4.25391 3.50905 4.25391 2.63672C4.25391 1.76439 4.96361 1.05469 5.83594 1.05469ZM6.89062 16.9453H2.63672C2.34594 16.9453 2.10938 16.7087 2.10938 16.418V8.4375H6.89062V16.9453ZM6.89062 7.38281H1.58203C1.29125 7.38281 1.05469 7.14625 1.05469 6.85547V5.80078C1.05469 5.51 1.29125 5.27344 1.58203 5.27344H6.89062V7.38281ZM10.0547 16.9453H7.94531V5.27344C8.05581 5.27344 9.44859 5.27344 10.0547 5.27344V16.9453ZM15.8906 16.418C15.8906 16.7087 15.6541 16.9453 15.3633 16.9453H11.1094V8.4375H15.8906V16.418ZM16.9453 6.85547C16.9453 7.14625 16.7087 7.38281 16.418 7.38281H11.1094V5.27344H16.418C16.7087 5.27344 16.9453 5.51 16.9453 5.80078V6.85547Z" fill="black"/>
      </g>
      <defs>
      <clipPath id="clip0_36_3401">
      <rect width="18" height="18" fill="white"/>
      </clipPath>
      </defs>
      </svg>
    </div>
    <div class="gift-title">FREE GIFT when you subscribe</div>
  </div>

  <div class="gift-content">
    <div class="gift-image">
      <img src="https://cdn.shopify.com/s/files/1/0064/3088/3930/files/Sweet_Bee_Organics_Mushroom_Whisk-02.jpg?v=1751358292" alt="Mushroom Whisk">
    </div>
    <div class="gift-info">
      <div class="gift-product-title">FREE Mushroom Mixer</div>
      <div class="gift-price">
        <span class="price-old">£22.00</span>
        <span class="price-new">FREE</span>
      </div>
    </div>
  </div>
</div>
<div style="display: none;" class="glow-free-gift-banner">
  <div class="gift-header">
    <div class="gift-icon">
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
      <g clip-path="url(#clip0_36_3401)">
      <path d="M16.418 4.21875H14.272C14.6038 3.77775 14.8008 3.22984 14.8008 2.63672C14.8008 1.18283 13.6179 0 12.1641 0C11.2883 0 10.6425 0.31377 10.1317 0.987434C9.70439 1.55106 9.39192 2.338 9 3.33229C8.60808 2.33796 8.29561 1.55106 7.86829 0.987434C7.3575 0.31377 6.71171 0 5.83594 0C4.38205 0 3.19922 1.18283 3.19922 2.63672C3.19922 3.22984 3.3962 3.77775 3.728 4.21875H1.58203C0.709699 4.21875 0 4.92845 0 5.80078V6.85547C0 7.54295 0.440895 8.12911 1.05469 8.3468V16.418C1.05469 17.2903 1.76439 18 2.63672 18H15.3633C16.2356 18 16.9453 17.2903 16.9453 16.418V8.3468C17.5591 8.12911 18 7.54295 18 6.85547V5.80078C18 4.92845 17.2903 4.21875 16.418 4.21875ZM9.96799 3.75258C10.7758 1.70304 11.083 1.05469 12.1641 1.05469C13.0364 1.05469 13.7461 1.76439 13.7461 2.63672C13.7461 3.50905 13.0364 4.21875 12.1641 4.21875H9.78353C9.84814 4.0565 9.90977 3.9003 9.96799 3.75258ZM5.83594 1.05469C6.91703 1.05469 7.22415 1.70304 8.03201 3.75258C8.09023 3.9003 8.15186 4.0565 8.21647 4.21875H5.83594C4.96361 4.21875 4.25391 3.50905 4.25391 2.63672C4.25391 1.76439 4.96361 1.05469 5.83594 1.05469ZM6.89062 16.9453H2.63672C2.34594 16.9453 2.10938 16.7087 2.10938 16.418V8.4375H6.89062V16.9453ZM6.89062 7.38281H1.58203C1.29125 7.38281 1.05469 7.14625 1.05469 6.85547V5.80078C1.05469 5.51 1.29125 5.27344 1.58203 5.27344H6.89062V7.38281ZM10.0547 16.9453H7.94531V5.27344C8.05581 5.27344 9.44859 5.27344 10.0547 5.27344V16.9453ZM15.8906 16.418C15.8906 16.7087 15.6541 16.9453 15.3633 16.9453H11.1094V8.4375H15.8906V16.418ZM16.9453 6.85547C16.9453 7.14625 16.7087 7.38281 16.418 7.38281H11.1094V5.27344H16.418C16.7087 5.27344 16.9453 5.51 16.9453 5.80078V6.85547Z" fill="black"/>
      </g>
      <defs>
      <clipPath id="clip0_36_3401">
      <rect width="18" height="18" fill="white"/>
      </clipPath>
      </defs>
      </svg>
    </div>
    <div class="gift-title">FREE GIFT when you subscribe (Only for 200ml and 500ml)</div>
  </div>

  <div class="gift-content">
    <div class="gift-image">
      <img src="https://cdn.shopify.com/s/files/1/0064/3088/3930/files/image_3.png?v=1752483755" alt="Free gift Magnesium Butter">
    </div>
    <div class="gift-info">
      <div class="gift-product-title">Sweet Sleep Magnesium Butter (30ml)</div>
      <div class="gift-price">
        <span class="price-old">£15.00</span>
        <span class="price-new">FREE</span>
      </div>
    </div>
  </div>
</div>
${unsafeHTML(
      (
        this.selectedSellingPlanGroup?.name === 'Prepaid'
          ? this.options?.additional_subscription_content_prepaid
          : this.options?.additional_subscription_content
      )
        ?.replaceAll(
          '[discount]',
          this.options?.discount_format === 'absolute'
            ? this.money(this.discount(this.selectedSellingPlan).absolute)
            : this.discount(this.selectedSellingPlan).percent + '%'
        )
        ?.replaceAll(
          '[future_price_adjustments]',
          this.postCheckoutDiscountsText(this.selectedSellingPlan) || ''
        ) || ''
    )}
      </div>
    `
  }

  // SECTION: Discount Functions
  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discountText(selling_plan) {
    const discount = this.discount(selling_plan)
    const hasInvalidDiscount = Object.values(discount).some(value => value === 0 || value === Infinity || value.toString().includes('-'))

    if (hasInvalidDiscount) {
      return '' // Return empty if any invalid condition is met
    } else {
      return unsafeHTML(
        this.options?.discount_text
          .replaceAll('[discount]', this.options?.discount_format === 'absolute' ? this.money(discount.absolute) : discount.percent + '%')
          .replaceAll('[future_price_adjustments]', this.postCheckoutDiscountsText(selling_plan) || '')
      )
    }
  }

  discount(selling_plan) {
    if (!selling_plan) return { percent: 0, amount: 0 }
    return this.getDiscountFromPriceAdjustment(selling_plan, selling_plan.price_adjustments[0])
  }

  postCheckoutPriceAdjustments(selling_plan) {
    const { price_adjustments } = selling_plan
    const postCheckoutPriceAdjustments = price_adjustments.filter(({ position }) => position !== 1)
    const discounts = postCheckoutPriceAdjustments.map(price_adjustment => this.getDiscountFromPriceAdjustment(selling_plan, price_adjustment))
    return discounts
  }

  postCheckoutDiscountsText(sellingPlan = this.selectedSellingPlan) {
    if (!sellingPlan || !this.options?.future_price_adjustments_text || this.future_price_adjustments_text == '') return

    const postCheckoutPriceAdjustments = this.postCheckoutPriceAdjustments(sellingPlan)
    if (!postCheckoutPriceAdjustments.length) return

    return postCheckoutPriceAdjustments.map((discount, index) =>
      this.options?.future_price_adjustments_text
        .replaceAll('[discount]', this.options?.discount_format === 'absolute' ? this.money(discount.absolute) : discount.percent + '%')
        .replaceAll('[order_count]', index + 1)
        .replaceAll('[order_count_ordinal]', this.appendOrdinalSuffix(index + 1))
    )
  }

  getDiscountFromPriceAdjustment(selling_plan, price_adjustment) {
    const discount = {
      percent: 0,
      amount: 0,
      absolute: 0,
    }

    let multiplier = 1

    let sellingPlanGroup = this.product.selling_plan_groups.find(group => group.selling_plans.some(plan => plan.id == selling_plan.id))

    if (sellingPlanGroup?.name === 'Prepaid') {
      const str = selling_plan.name // replace with your string
      const intervalDate = /\b\d+\s*(days|weeks|months|years)\b/gi
      const intervalDateMatches = str.match(intervalDate)

      if (intervalDateMatches?.length) {
        const intervals = intervalDateMatches[0].match(/\d+/g)
        multiplier = intervals[0] / (intervals[1] || 1)
        multiplier = multiplier > 1 ? multiplier : 1
      }
    }

    const price = this.selectedVariant.price
    const compareAtPrice = this.selectedVariant.compare_at_price && this.selectedVariant.compare_at_price > price ? this.selectedVariant.compare_at_price : price

    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = price_adjustment.value
        discount.absolute = Math.round((price * price_adjustment.value) / 100.0)
        discount.amount = Math.round((price * price_adjustment.value) / 100.0)
        break
      case 'fixed_amount':
        discount.percent = Math.round(((price_adjustment.value * 1.0) / price) * 100.0)
        discount.absolute = price_adjustment.value
        discount.amount = price_adjustment.value
        break
      case 'price':
        discount.percent = Math.round((((compareAtPrice * multiplier - price_adjustment.value) * 1.0) / (compareAtPrice * multiplier)) * 100.0)
        discount.absolute = compareAtPrice * multiplier - price_adjustment.value
        discount.amount = price - price_adjustment.value
        break
    }

    return discount
  }

  /**
   * Formats a number of cents into a currency string using Shopify format string.
   *
   * https://help.shopify.com/en/manual/international/pricing/currency-formatting#currency-formatting-options
   *
   * Originally from: https://gist.github.com/stewartknapman/8d8733ea58d2314c373e94114472d44c
   *
   * @param cents - The number of cents to format.
   * @param formatString - The format string to use.
   * @returns The formatted currency string.
   */
  money(cents, formatString = this.moneyFormat) {
    const placeholderRegex = /{{\s*(\w+)\s*}}/

    /**
     * Formats a number of cents into a currency string using the provided
     * precision, thousands separator, and decimal separator.
     * @param number - The number of cents to format.
     * @param precision - The number of decimal places to include.
     * @param thousands - The character to use as the thousands separator.
     * @param decimal - The character to use as the decimal separator.
     * @returns The formatted currency string.
     */
    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) {
        return '0'
      }

      const numString = (number / 100.0).toFixed(precision)
      const parts = numString.split('.')
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands)
      const cents = parts[1] ? decimal + parts[1] : ''
      return dollars + cents
    }

    return formatString.replace(placeholderRegex, (match, placeholder) => {
      switch (placeholder) {
        case 'amount':
          // Ex.	1,134.65
          return formatWithDelimiters(cents, 2)
        case 'amount_no_decimals':
          // Ex. 1,135
          return formatWithDelimiters(cents, 0)
        case 'amount_with_comma_separator':
          // Ex. 1.134,65
          return formatWithDelimiters(cents, 2, '.', ',')
        case 'amount_no_decimals_with_comma_separator':
          // Ex. 1.135
          return formatWithDelimiters(cents, 0, '.', ',')
        case 'amount_with_apostrophe_separator':
          // Ex. 1'134.65
          return formatWithDelimiters(cents, 2, "'", '.')
        case 'amount_no_decimals_with_space_separator':
          // Ex. 1 135
          return formatWithDelimiters(cents, 0, ' ')
        case 'amount_with_space_separator':
          // 1 134,65
          return formatWithDelimiters(cents, 2, ' ', ',')
        case 'amount_with_period_and_space_separator':
          // 1 134.65
          return formatWithDelimiters(cents, 2, ' ', '.')
        default:
          return match
      }
    })
  }

  // !SECTION: Discount Functions
  price(selling_plan, formatted = true) {
    if (this.rule) {
      return formatted
        ? this.money(this.selectedVariant.price - this.discount(selling_plan).amount - this.selectedVariant.price * (this.rule.discountAmount / 100))
        : this.selectedVariant.price - this.discount(selling_plan).amount - this.selectedVariant.price * (this.rule.discountAmount / 100)
    }

    return formatted ? this.money(this.selectedVariant.price - this.discount(selling_plan).amount) : this.selectedVariant.price - this.discount(selling_plan).amount
  }

  // Updates element data to be registered by forms
  updateForm() {
    let $sellingPlan = this.querySelector('input[name=selling_plan]')

    if ($sellingPlan) {
      $sellingPlan.value = this.selectedSellingPlan ? this.selectedSellingPlan.id : ''
      $sellingPlan.dispatchEvent(new Event('change'))
    }
  }

  // SECTION: Additional Functionality
  updateURLParams() {
    const url = new URL(window.location.href)

    if (this.selectedSellingPlan) {
      url.searchParams.set('selling_plan', this.selectedSellingPlan.id)
      window.history.replaceState({}, '', url.href)
    } else {
      url.searchParams.delete('selling_plan')
      window.history.replaceState({}, '', url.href)
    }
  }

  // Runs a fetch request to add the selectedVariant to the cart with the passed quantity and selectedSellingPlan
  addToCart(quantity) {
    const items = [
      {
        id: this.selectedVariant.id,
        quantity: quantity,
        ...(this.selectedSellingPlan && {
          selling_plan: this.selectedSellingPlan?.id,
        }),
      },
    ]

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items,
      }),
    })
      .then(response => response.json())
      .then(response => {
        this.log('Added item to cart: ', response)
        //dispatch CustomEvent to tell document that an item was added to cart
        const event = new CustomEvent(`skio::added-to-cart`, {
          bubbles: true,
          composed: true,
          detail: {
            response,
            key: this.key,
          },
        })

        this.dispatchEvent(event)
      })
      .catch(error => {
        this.error(`SKIO ${this.key} error adding item to cart: `, error)
      })
  }

  fetchProduct = handle => {
    return fetch(`/products/${handle}.js`)
      .then(response => response.json())
      .then(product => {
        this.product = product
        this.selectedVariant = product.variants[0]

        return product
      })
  }
  // !SECTION: Additional Functionality
}

if (!customElements.get('skio-plan-picker')) {
  customElements.define('skio-plan-picker', SkioPlanPicker)
}

if (window) window.SkioPlanPicker = SkioPlanPicker
